import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Loader2, BarChart3, Search, ShieldAlert, History, Settings2, Crown,
  Smartphone, Monitor, Globe, MousePointerClick, TrendingUp, Check, ArrowRight, HeartPulse,
} from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const SUB_TABS = [
  { key: "analytics", label: "Analytics", icon: BarChart3 },
  { key: "health", label: "Attribution Health", icon: HeartPulse },
  { key: "codes", label: "Code Explorer", icon: Search },
  { key: "fraud", label: "Fraud Alerts", icon: ShieldAlert },
  { key: "overrides", label: "Overrides", icon: History },
  { key: "settings", label: "Settings", icon: Settings2 },
];

function Stat({ label, value, sub, accent = "text-navy" }) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-4">
      <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{label}</div>
      <div className={`mt-1 font-heading text-2xl font-extrabold ${accent}`}>{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function Breakdown({ title, data, icon: Icon }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((s, [, v]) => s + v, 0) || 1;
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-4" data-testid={`breakdown-${title.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-2 mb-3 text-navy font-semibold text-sm"><Icon className="w-4 h-4 text-cobalt" /> {title}</div>
      {entries.length === 0 ? <p className="text-xs text-slate-400">No data yet.</p> : (
        <div className="space-y-2">
          {entries.slice(0, 6).map(([k, v]) => (
            <div key={k}>
              <div className="flex justify-between text-xs mb-0.5"><span className="text-slate-600 capitalize">{k}</span><span className="text-slate-400">{v}</span></div>
              <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-cobalt rounded-full" style={{ width: `${(v / total) * 100}%` }} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AnalyticsTab({ overview }) {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/referrals/analytics").then((r) => setA(r.data)).catch(() => {}); }, []);
  if (!a) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;
  const f = a.funnel || {};
  const maxTrend = Math.max(1, ...(a.trend || []).map((t) => t.scans));

  return (
    <div className="space-y-6" data-testid="referrals-analytics">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="QR / Link Scans" value={f.scans || 0} sub={`${f.conversion_rate || 0}% converted`} />
        <Stat label="Signups" value={f.signups || 0} sub={`${f.cases || 0} cases · ${f.claimed || 0} claimed`} />
        <Stat label="Attributed Revenue" value={money(f.revenue)} accent="text-green-600" />
        <Stat label="Open Fraud Alerts" value={overview?.open_fraud_alerts || 0} accent={(overview?.open_fraud_alerts || 0) > 0 ? "text-red-500" : "text-navy"} />
      </div>

      {/* Funnel */}
      <div className="rounded-3xl border border-slate-100 bg-white p-6" data-testid="referral-funnel">
        <h3 className="font-heading font-semibold text-navy mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-cobalt" /> Attribution Funnel</h3>
        <div className="flex items-center gap-2 overflow-x-auto">
          {[["Scans", f.scans], ["Signups", f.signups], ["Cases", f.cases], ["Claimed", f.claimed]].map(([label, val], i, arr) => (
            <div key={label} className="flex items-center gap-2">
              <div className="rounded-2xl bg-slate-50 border border-slate-100 px-5 py-3 text-center min-w-[92px]">
                <div className="font-heading text-2xl font-extrabold text-navy">{val || 0}</div>
                <div className="text-xs text-slate-400">{label}</div>
              </div>
              {i < arr.length - 1 && <ArrowRight className="w-4 h-4 text-slate-300 shrink-0" />}
            </div>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <Breakdown title="Device" data={a.by_device} icon={Smartphone} />
        <Breakdown title="Operating System" data={a.by_os} icon={Monitor} />
        <Breakdown title="Browser" data={a.by_browser} icon={Monitor} />
        <Breakdown title="Country" data={a.by_country} icon={Globe} />
        <Breakdown title="Landing / Source" data={a.by_source} icon={MousePointerClick} />
        <div className="rounded-2xl border border-slate-100 bg-white p-4">
          <div className="flex items-center gap-2 mb-3 text-navy font-semibold text-sm"><TrendingUp className="w-4 h-4 text-cobalt" /> Scan Trend</div>
          {(a.trend || []).length === 0 ? <p className="text-xs text-slate-400">No scans yet.</p> : (
            <div className="flex items-end gap-1 h-24">
              {a.trend.map((t) => (
                <div key={t.date} title={`${t.date}: ${t.scans}`} className="flex-1 bg-cobalt/70 rounded-t hover:bg-cobalt transition-colors" style={{ height: `${(t.scans / maxTrend) * 100}%` }} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent scans */}
      <div className="rounded-3xl border border-slate-100 bg-white p-6">
        <h3 className="font-heading font-semibold text-navy mb-3">Recent Scans</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-400 uppercase"><th className="py-2 pr-4">Code</th><th className="pr-4">Device</th><th className="pr-4">OS / Browser</th><th className="pr-4">Location</th><th className="pr-4">Converted</th><th>When</th></tr></thead>
            <tbody>
              {(a.recent_scans || []).length === 0 ? <tr><td colSpan={6} className="py-4 text-slate-400">No scans recorded.</td></tr> :
                a.recent_scans.map((s) => (
                  <tr key={s.scan_id} className="border-t border-slate-50" data-testid={`scan-row-${s.scan_id}`}>
                    <td className="py-2 pr-4 font-mono text-cobalt">{s.code}</td>
                    <td className="pr-4 capitalize">{s.device_type || "—"}</td>
                    <td className="pr-4 text-slate-500">{s.os || "—"} / {s.browser || "—"}</td>
                    <td className="pr-4 text-slate-500">{(s.location?.country && s.location.country !== "Unknown") ? `${s.location.country}${s.location.city ? ` · ${s.location.city}` : ""}` : "—"}</td>
                    <td className="pr-4">{s.converted ? <span className="text-green-600 font-medium">Yes</span> : <span className="text-slate-400">No</span>}</td>
                    <td className="text-slate-400 text-xs">{(s.created_at || "").replace("T", " ").slice(0, 16)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top performers */}
      <div className="grid md:grid-cols-2 gap-4">
        {[["Top Attorneys (Firm Codes)", overview?.top_attorneys], ["Top Affiliates", overview?.top_affiliates]].map(([title, rows]) => (
          <div key={title} className="rounded-3xl border border-slate-100 bg-white p-6">
            <h3 className="font-heading font-semibold text-navy mb-3">{title}</h3>
            {(rows || []).length === 0 ? <p className="text-sm text-slate-400">No data yet.</p> : (
              <div className="space-y-2">
                {rows.map((r) => (
                  <div key={r.code} className="flex items-center justify-between text-sm border-b border-slate-50 pb-2">
                    <div><span className="font-mono text-cobalt">{r.code}</span> <span className="text-slate-400">· {r.owner || "—"}</span></div>
                    <div className="flex gap-3 text-xs text-slate-500"><span>{r.clicks} clicks</span><span>{r.claimed}/{r.cases} claimed</span><span className="text-green-600 font-medium">{money(r.revenue)}</span></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const STATUS_STYLE = {
  healthy: { dot: "bg-green-500", chip: "bg-green-50 text-green-600", bar: "bg-green-500" },
  warning: { dot: "bg-amber-500", chip: "bg-amber-50 text-amber-600", bar: "bg-amber-500" },
  critical: { dot: "bg-red-500", chip: "bg-red-50 text-red-600", bar: "bg-red-500" },
};
const FLAG_SEV = { critical: "bg-red-50 text-red-600", high: "bg-orange-50 text-orange-600", medium: "bg-amber-50 text-amber-600", low: "bg-slate-100 text-slate-500" };

function HealthTab() {
  const [d, setD] = useState(null);
  const [filter, setFilter] = useState("all");
  useEffect(() => { api.get("/admin/referrals/health").then((r) => setD(r.data)).catch(() => setD({ summary: {}, codes: [] })); }, []);
  if (!d) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;
  const s = d.summary || {};
  const rows = (d.codes || []).filter((r) => filter === "all" || r.status === filter);

  return (
    <div data-testid="referrals-health">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="Avg Health Score" value={`${s.avg_score ?? 100}`} accent={(s.avg_score ?? 100) >= 80 ? "text-green-600" : (s.avg_score ?? 100) >= 50 ? "text-amber-600" : "text-red-500"} sub={`${s.total || 0} codes`} />
        <Stat label="Healthy" value={s.healthy || 0} accent="text-green-600" />
        <Stat label="Needs Review" value={s.warning || 0} accent="text-amber-600" />
        <Stat label="Critical" value={s.critical || 0} accent="text-red-500" />
      </div>

      <div className="flex gap-2 mb-4">
        {["all", "critical", "warning", "healthy"].map((k) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`health-filter-${k}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-all ${filter === k ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            {k}
          </button>
        ))}
      </div>

      {rows.length === 0 ? <p className="text-sm text-slate-400">No codes match this filter.</p> : (
        <div className="space-y-3">
          {rows.map((r) => {
            const st = STATUS_STYLE[r.status] || STATUS_STYLE.healthy;
            return (
              <div key={r.code} className="rounded-2xl border border-slate-100 bg-white p-4" data-testid={`health-row-${r.code}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${st.dot}`} />
                    <span className="font-mono text-cobalt font-semibold">{r.code}</span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${r.type === "firm" ? "bg-navy/10 text-navy" : "bg-cobalt/10 text-cobalt"}`}>{r.type}</span>
                    <span className="text-xs text-slate-400">· {r.owner || "—"}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${st.chip}`}>{r.status}</span>
                    <div className="text-right">
                      <div className="font-heading text-xl font-extrabold text-navy leading-none">{r.score}</div>
                      <div className="text-[10px] text-slate-400 uppercase">health</div>
                    </div>
                  </div>
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full rounded-full ${st.bar}`} style={{ width: `${r.score}%` }} /></div>
                <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-500">
                  <span>{r.clicks} scans</span><span>{r.signups} signups ({r.conversion_rate}%)</span>
                  <span>{r.claimed}/{r.cases} claimed ({r.claim_rate}%)</span>
                </div>
                {r.flags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {r.flags.map((f) => (
                      <span key={f.key} className={`text-xs font-medium px-2 py-0.5 rounded-full ${FLAG_SEV[f.severity] || FLAG_SEV.low}`} data-testid={`health-flag-${r.code}-${f.key}`}>{f.label}</span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CodesTab() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const search = async () => {
    if (!q.trim()) return;
    try { const { data } = await api.get(`/admin/referrals/lookup?q=${encodeURIComponent(q.trim())}`); setRows(data); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  return (
    <div data-testid="referrals-codes">
      <div className="flex gap-2 mb-4">
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} data-testid="code-search-input" placeholder="Search a referral / firm / affiliate code…" className="flex-1 border border-slate-200 rounded-full px-4 py-2.5 text-sm outline-none focus:border-cobalt uppercase" />
        <button onClick={search} data-testid="code-search-btn" className="px-5 py-2.5 rounded-full bg-cobalt text-white text-sm font-medium hover:bg-cobalt-hover flex items-center gap-2"><Search className="w-4 h-4" /> Search</button>
      </div>
      {rows === null ? <p className="text-sm text-slate-400">Search a code to view owner, type, clicks, signups and attributed clients.</p> :
        rows.length === 0 ? <p className="text-sm text-slate-400">No matching codes.</p> : (
          <div className="space-y-3">
            {rows.map((r) => (
              <div key={r.code} className="rounded-2xl border border-slate-100 bg-white p-4" data-testid={`code-result-${r.code}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div><span className="font-mono text-cobalt font-semibold">{r.code}</span> <span className={`ml-1 text-xs font-semibold px-2 py-0.5 rounded-full ${r.type === "firm" ? "bg-navy/10 text-navy" : "bg-cobalt/10 text-cobalt"}`}>{r.type}</span></div>
                  <div className="text-xs text-slate-500">{r.owner_name || "—"} · {r.owner_email || "—"}</div>
                </div>
                <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-500">
                  <span>Clicks {r.clicks || 0}</span><span>Signups {r.signups || 0}</span>
                  <span>Attributed clients {r.attributed_clients || 0}</span>
                  <span>{r.active ? <span className="text-green-600">Active</span> : <span className="text-red-500">Inactive</span>}</span>
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

const SEV = { high: "bg-red-50 text-red-600", medium: "bg-amber-50 text-amber-600", low: "bg-slate-100 text-slate-500" };

function FraudTab() {
  const [alerts, setAlerts] = useState(null);
  const load = () => api.get("/admin/fraud-alerts").then((r) => setAlerts(r.data)).catch(() => setAlerts([]));
  useEffect(() => { load(); }, []);
  const resolve = async (id) => { try { await api.post(`/admin/fraud-alerts/${id}/resolve`); toast.success("Alert resolved"); load(); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } };
  if (alerts === null) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;
  return (
    <div data-testid="referrals-fraud">
      {alerts.length === 0 ? <p className="text-sm text-slate-400">No fraud alerts. The engine flags self-referrals, duplicate devices and suspicious IP clusters automatically.</p> : (
        <div className="space-y-3">
          {alerts.map((f) => (
            <div key={f.alert_id} className="rounded-2xl border border-slate-100 bg-white p-4" data-testid={`fraud-alert-${f.alert_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${SEV[f.severity] || SEV.low}`}>{f.severity}</span>
                  <span className="font-semibold text-navy capitalize">{(f.type || "").replace(/_/g, " ")}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${f.status === "open" ? "bg-red-50 text-red-500" : "bg-green-50 text-green-600"}`}>{f.status}</span>
                </div>
                {f.status === "open" && <button onClick={() => resolve(f.alert_id)} data-testid={`resolve-fraud-${f.alert_id}`} className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-green-50 text-green-600 text-xs font-medium hover:bg-green-100"><Check className="w-3.5 h-3.5" /> Resolve</button>}
              </div>
              <p className="text-sm text-slate-600 mt-2">{f.detail}</p>
              <div className="mt-1 flex flex-wrap gap-3 text-xs text-slate-400">
                {f.code && <span>Code {f.code}</span>}{f.ip_address && <span>IP {f.ip_address}</span>}
                {f.device_id && <span>Device {f.device_id}</span>}
                <span>{(f.created_at || "").replace("T", " ").slice(0, 16)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OverridesTab({ isSuper }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState({ client_id: "", new_code: "", reason: "" });
  const load = () => api.get("/admin/referrals/overrides").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const submit = async () => {
    try { await api.post("/admin/referrals/override", { ...form, new_code: form.new_code.toUpperCase() }); toast.success("Attribution overridden (immutable log created)"); setForm({ client_id: "", new_code: "", reason: "" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  return (
    <div data-testid="referrals-overrides">
      {isSuper ? (
        <div className="rounded-3xl border border-cobalt/20 bg-cobalt/5 p-5 mb-6">
          <div className="flex items-center gap-2 mb-3 text-navy font-semibold"><Crown className="w-4 h-4 text-cobalt" /> Super Admin Re-attribution</div>
          <p className="text-xs text-slate-500 mb-3">Overrides are immutable & versioned — no history is deleted. Re-points the client's cases and unpaid commissions to the new code.</p>
          <div className="grid sm:grid-cols-3 gap-2 mb-2">
            <input value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="override-client-id" placeholder="Client user_id" className="border border-slate-200 rounded-full px-4 py-2.5 text-sm outline-none focus:border-cobalt" />
            <input value={form.new_code} onChange={(e) => setForm({ ...form, new_code: e.target.value.toUpperCase() })} data-testid="override-new-code" placeholder="New code" className="border border-slate-200 rounded-full px-4 py-2.5 text-sm outline-none focus:border-cobalt uppercase" />
            <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="override-reason" placeholder="Reason (audited)" className="border border-slate-200 rounded-full px-4 py-2.5 text-sm outline-none focus:border-cobalt" />
          </div>
          <button onClick={submit} disabled={!form.client_id || !form.new_code || form.reason.length < 3} data-testid="override-submit" className="px-5 py-2.5 rounded-full bg-cobalt text-white text-sm font-medium hover:bg-cobalt-hover disabled:opacity-40">Apply Override</button>
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4 mb-6 text-sm text-slate-500 flex items-center gap-2"><Crown className="w-4 h-4 text-slate-400" /> Only Super Admins can create attribution overrides. History is read-only below.</div>
      )}
      <h3 className="font-heading font-semibold text-navy mb-3">Override History</h3>
      {rows === null ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div> :
        rows.length === 0 ? <p className="text-sm text-slate-400">No overrides recorded.</p> : (
          <div className="space-y-2">
            {rows.map((o) => (
              <div key={o.override_id} className="rounded-2xl border border-slate-100 bg-white p-4 text-sm" data-testid={`override-${o.override_id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-slate-400">{o.previous_code || "organic"}</span>
                  <ArrowRight className="w-3.5 h-3.5 text-slate-300" />
                  <span className="font-mono text-cobalt font-semibold">{o.new_code}</span>
                  <span className="text-xs text-slate-400">· {o.new_type}</span>
                </div>
                <p className="text-slate-600 mt-1">{o.reason}</p>
                <div className="text-xs text-slate-400 mt-1">by {o.admin_name || o.admin_id} · client {o.client_id} · {(o.created_at || "").replace("T", " ").slice(0, 16)}</div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

function SettingsTab({ isSuper }) {
  const [days, setDays] = useState(null);
  const [users, setUsers] = useState([]);
  useEffect(() => {
    api.get("/admin/attribution-window").then((r) => setDays(r.data.days)).catch(() => {});
    if (isSuper) api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  }, [isSuper]);
  const saveWindow = async () => { try { await api.put("/admin/attribution-window", { days: Number(days) }); toast.success(`Attribution window set to ${days} days`); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } };
  const toggleSuper = async (u) => {
    try { await api.post(`/admin/users/${u.user_id}/super-admin`, { grant: !u.super_admin }); toast.success(`Super Admin ${u.super_admin ? "revoked" : "granted"}`); setUsers((prev) => prev.map((x) => x.user_id === u.user_id ? { ...x, super_admin: !u.super_admin } : x)); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  return (
    <div className="space-y-6" data-testid="referrals-settings">
      <div className="rounded-3xl border border-slate-100 bg-white p-6">
        <h3 className="font-heading font-semibold text-navy mb-2">Attribution Window</h3>
        <p className="text-xs text-slate-500 mb-3">First-touch attribution is honored for this many days after the first scan/click.</p>
        <div className="flex items-center gap-2">
          <input type="number" min={1} max={730} value={days ?? ""} onChange={(e) => setDays(e.target.value)} data-testid="attribution-window-input" className="w-28 border border-slate-200 rounded-full px-4 py-2.5 text-sm outline-none focus:border-cobalt" />
          <span className="text-sm text-slate-500">days</span>
          <button onClick={saveWindow} data-testid="save-attribution-window" className="ml-2 px-5 py-2.5 rounded-full bg-cobalt text-white text-sm font-medium hover:bg-cobalt-hover">Save</button>
        </div>
      </div>

      {isSuper && (
        <div className="rounded-3xl border border-slate-100 bg-white p-6" data-testid="super-admin-mgmt">
          <h3 className="font-heading font-semibold text-navy mb-2 flex items-center gap-2"><Crown className="w-4 h-4 text-cobalt" /> Super Admin Access</h3>
          <p className="text-xs text-slate-500 mb-3">Super Admins can override attribution locks. Grant sparingly.</p>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {users.filter((u) => (u.roles || []).includes("admin")).map((u) => (
              <div key={u.user_id} className="flex items-center justify-between rounded-2xl border border-slate-100 p-3 text-sm" data-testid={`super-row-${u.user_id}`}>
                <div><span className="font-medium text-navy">{u.name}</span> <span className="text-slate-400">· {u.email}</span> {u.super_admin && <span className="ml-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-cobalt/10 text-cobalt">Super Admin</span>}</div>
                <button onClick={() => toggleSuper(u)} data-testid={`toggle-super-${u.user_id}`} className={`px-3 py-1.5 rounded-full text-xs font-medium ${u.super_admin ? "bg-red-50 text-red-500 hover:bg-red-100" : "bg-cobalt text-white hover:bg-cobalt-hover"}`}>{u.super_admin ? "Revoke" : "Grant"}</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminReferrals() {
  const { user } = useAuth();
  const isSuper = !!user?.super_admin;
  const [sub, setSub] = useState("analytics");
  const [overview, setOverview] = useState(null);

  useEffect(() => { api.get("/admin/referrals/overview").then((r) => setOverview(r.data)).catch(() => {}); }, []);

  return (
    <div data-testid="admin-referrals-tab">
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Stat label="Commissions Pending" value={money(overview.commissions?.pending)} accent="text-amber-600" />
          <Stat label="Commissions Available" value={money(overview.commissions?.available)} accent="text-cobalt" />
          <Stat label="Commissions Paid" value={money(overview.commissions?.paid)} accent="text-green-600" />
          <Stat label="Overrides Logged" value={overview.override_count || 0} sub={`${overview.open_fraud_alerts || 0} open fraud alerts`} />
        </div>
      )}

      <div className="flex gap-2 overflow-x-auto pb-1 mb-6">
        {SUB_TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setSub(key)} data-testid={`referrals-subtab-${key}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${sub === key ? "bg-cobalt text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            <Icon className="w-4 h-4" /> {label}
            {key === "fraud" && (overview?.open_fraud_alerts || 0) > 0 && <span className="ml-1 text-xs font-bold px-1.5 rounded-full bg-red-500 text-white">{overview.open_fraud_alerts}</span>}
          </button>
        ))}
      </div>

      {sub === "analytics" && <AnalyticsTab overview={overview} />}
      {sub === "health" && <HealthTab />}
      {sub === "codes" && <CodesTab />}
      {sub === "fraud" && <FraudTab />}
      {sub === "overrides" && <OverridesTab isSuper={isSuper} />}
      {sub === "settings" && <SettingsTab isSuper={isSuper} />}
    </div>
  );
}
